/**
 * RelatedReferences — single source of truth for the "Related
 * Reference Materials" card that appears at the bottom of every
 * reference page (LEI, PE, ROE, Maturity).
 *
 * Before this component, each ref page hard-coded its sibling list.
 * Adding a future ref (e.g. KPI Reference) meant editing 4 files.
 * Now: edit `REFERENCES` here, every page picks it up.
 *
 * Props:
 *   - `activeRef` : key of the current page (lei | pe | roe |
 *                   maturity | transfer | research). Filtered OUT
 *                   of the rendered list so we don't link to self.
 *                   If omitted, all references render — handy for
 *                   placing this card on health/dashboard pages.
 *   - `extras`    : optional list of `{href, label}` to append.
 *                   Used by ROE Reference to keep its existing
 *                   ROE Health / ROE Modeller links.
 *
 * Style mirrors the original hand-rolled card (slate-50 bg, white
 * pill links). Uses anchor tags rather than `useNavigate` so
 * pages without router context can still drop this in safely.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';


const REFERENCES = [
  { key: 'lei', href: '/lei-reference', label: 'LEI Reference' },
  { key: 'pe', href: '/people-engine/reference', label: 'PE Reference' },
  { key: 'roe', href: '/roe-reference', label: 'ROE Reference' },
  { key: 'maturity', href: '/maturity-reference', label: 'Maturity Reference' },
  { key: 'transfer', href: '/transfer-methodology', label: 'Transfer Methodology' },
  { key: 'research', href: '/research', label: 'Research & Evidence' },
];


export default function RelatedReferences({ activeRef, extras = [] }) {
  const items = REFERENCES.filter((r) => r.key !== activeRef);
  return (
    <Card className="bg-slate-50" data-testid="related-references-card">
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-slate-700 mb-3">
          Related Reference Materials
        </p>
        <div className="flex flex-wrap gap-2">
          {items.map((r) => (
            <a
              key={r.href}
              href={r.href}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
              data-testid={`related-ref-${r.key}`}
            >
              {r.label}
            </a>
          ))}
          {extras.map((e) => (
            <a
              key={e.href}
              href={e.href}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
            >
              {e.label}
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
